import { useMemo, useState } from "react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from "recharts";
import { useModuleData } from "@/lib/useData";
import { PALETTE, COLORS, fmt, uniq } from "@/lib/api";
import { PageHeader, Panel, Kpi, ChartTooltip, Loading } from "@/components/dash";
import { DataTable } from "@/components/DataTable";
import { AIInsights } from "@/components/AIInsights";
import { ProductGate } from "@/components/ProductGate";
import { Input } from "@/components/ui/input";

const COMPONENTS = ["ISBL", "OSBL", "Other Project Costs", "Working Capital"];
const grid = <CartesianGrid stroke={COLORS.grid} vertical={false} />;

function Chips({ options, value, onChange, testId }) {
  const toggle = (o) => onChange(value.includes(o) ? value.filter((v) => v !== o) : [...value, o]);
  return (
    <div className="flex flex-wrap gap-1.5" data-testid={testId}>
      {options.map((o, i) => (
        <button key={o} data-testid={`${testId}-${i}`} onClick={() => toggle(o)}
          className={`text-xs px-2.5 py-1 rounded border transition-colors text-left ${value.includes(o) ? "bg-[#1074BA]/20 border-[#1074BA] text-white" : "border-slate-700 text-slate-400 hover:text-white"}`}>{o}</button>
      ))}
    </div>
  );
}

function CapexView({ product, selector }) {
  const { data, isLoading } = useModuleData("capex", product);
  const cases = useMemo(() => Object.values((data?.rows || []).reduce((m, r) => {
    const k = `${r.ref}|${r.process}|${r.location}`;
    m[k] = m[k] || { key: k, ref: r.ref, process: r.process, location: r.location, capacity: r.capacity, year: r.year, parts: {} };
    m[k].parts[r.capital_type] = (m[k].parts[r.capital_type] || 0) + r.value_musd;
    return m;
  }, {})), [data]);
  const processes = uniq(cases.map((c) => c.process));
  const locations = uniq(cases.map((c) => c.location));
  const [selP, setSelP] = useState(null);
  const [selL, setSelL] = useState(null);
  const [targets, setTargets] = useState(["", "", "", "", ""]);
  const [exp, setExp] = useState(0.6);
  const [esc, setEsc] = useState(0);
  const [toYear, setToYear] = useState("");
  if (isLoading) return <Loading />;
  const P = selP ?? processes.slice(0, 1), L = selL ?? locations;
  const picked = cases.filter((c) => P.includes(c.process) && L.includes(c.location));
  const tgts = targets.map(Number).filter((t) => t > 0);
  const results = picked.flatMap((c) => {
    const total = Object.values(c.parts).reduce((a, b) => a + b, 0);
    const caps = tgts.length ? tgts : [c.capacity];
    return caps.map((t) => {
      const escF = toYear && esc ? (1 + esc / 100) ** (Number(toYear) - c.year) : 1;
      const scaled = total * (t / c.capacity) ** exp * escF;
      return { id: `${c.key}|${t}`, label: `${c.location} · ${t} kt`, process: c.process, location: c.location, base_cap: c.capacity, base_year: c.year, target: t, base_total: total, total: scaled,
        ...Object.fromEntries(COMPONENTS.map((k) => [k, total ? ((c.parts[k] || 0) / total) * scaled : 0])), per_kt: scaled / t };
    });
  });

  return (
    <div className="space-y-6">
      <PageHeader eyebrow="CAPEX Calculator" title={product} desc="Run what-if scenarios on total plant capital using the scale-factor (six-tenths) rule; split follows each reference case's ISBL/OSBL/Other/Working-capital ratios.">
        {selector}
      </PageHeader>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Panel title="Step 1 · Process & Location" sub={`${cases.length} reference cases`} testId="capex-inputs">
          <p className="text-xs font-mono uppercase text-slate-500 mb-2">Process (multi-select)</p>
          <Chips testId="capex-process-chips" options={processes} value={P} onChange={setSelP} />
          <p className="text-xs font-mono uppercase text-slate-500 mt-5 mb-2">Location (multi-select)</p>
          <Chips testId="capex-location-chips" options={locations} value={L} onChange={setSelL} />
        </Panel>
        <Panel title="Step 2 · Target Capacity & Assumptions" testId="capex-assumptions">
          <p className="text-xs font-mono uppercase text-slate-500 mb-2">Target capacities (ktpa)</p>
          <div className="grid grid-cols-5 gap-2">
            {targets.map((t, i) => (
              <Input key={i} data-testid={`capex-target-${i + 1}`} type="number" min="0" placeholder={`T${i + 1}`} value={t}
                onChange={(e) => setTargets(targets.map((x, j) => (j === i ? e.target.value : x)))} className="h-9 bg-slate-900 border-slate-700 font-mono text-sm" />
            ))}
          </div>
          <div className="grid grid-cols-3 gap-3 mt-5">
            <label className="text-xs text-slate-400">Scale exponent<Input data-testid="capex-exponent" type="number" step="0.05" value={exp} onChange={(e) => setExp(Number(e.target.value) || 0.6)} className="h-9 mt-1 bg-slate-900 border-slate-700 font-mono" /></label>
            <label className="text-xs text-slate-400">Escalation %/yr<Input data-testid="capex-escalation" type="number" step="0.5" value={esc} onChange={(e) => setEsc(Number(e.target.value) || 0)} className="h-9 mt-1 bg-slate-900 border-slate-700 font-mono" /></label>
            <label className="text-xs text-slate-400">Escalate to year<Input data-testid="capex-to-year" type="number" placeholder="e.g. 2026" value={toYear} onChange={(e) => setToYear(e.target.value)} className="h-9 mt-1 bg-slate-900 border-slate-700 font-mono" /></label>
          </div>
          <p className="text-[11px] text-slate-500 mt-4 font-mono">Total = Base Total × (Target / Base Capacity)^{exp}{toYear && esc ? ` × (1+${esc}%)^(${toYear} − base year)` : ""}</p>
        </Panel>
        <AIInsights module="capex" product={product} />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Kpi testId="capex-kpi-scenarios" label="Scenarios" value={results.length} />
        <Kpi testId="capex-kpi-min" label="Lowest total capital" value={fmt(Math.min(...results.map((r) => r.total)), 1)} unit="USD mn" accent={COLORS.cyan} />
        <Kpi testId="capex-kpi-max" label="Highest total capital" value={fmt(Math.max(...results.map((r) => r.total)), 1)} unit="USD mn" accent={COLORS.orange} />
      </div>
      <Panel title="Step 3 · Estimated Total Plant Capital" sub="USD million, split by component" testId="capex-result-chart">
        <ResponsiveContainer width="100%" height={360}>
          <BarChart data={results}>{grid}<XAxis dataKey="label" stroke="#334155" interval={0} tick={{ fontSize: 9 }} /><YAxis stroke="#334155" />
            <Tooltip content={<ChartTooltip unit="USD mn" digits={1} />} cursor={{ fill: "rgba(255,255,255,0.04)" }} /><Legend />
            {COMPONENTS.map((k, i) => <Bar key={k} dataKey={k} stackId="a" fill={PALETTE[i]} />)}
          </BarChart>
        </ResponsiveContainer>
      </Panel>
      <Panel title="Scenario Results" sub="USD million">
        <DataTable testId="capex-result-table" rows={results} columns={[
          { key: "process", label: "Process" }, { key: "location", label: "Location" }, { key: "base_cap", label: "Base kt", num: true }, { key: "base_year", label: "Base yr", num: true },
          { key: "target", label: "Target kt", num: true }, ...COMPONENTS.map((k) => ({ key: k, label: k, num: true, render: (r) => fmt(r[k], 1) })),
          { key: "total", label: "Total", num: true, render: (r) => <span className="text-[#F58B23] font-bold">{fmt(r.total, 1)}</span> },
          { key: "per_kt", label: "USD mn/kt", num: true, render: (r) => fmt(r.per_kt, 3) }]} />
      </Panel>
      <Panel title="Methodology" sub="How estimates are calculated">
        <ul className="text-sm text-slate-400 space-y-1.5 list-disc pl-5">
          <li>Reference cases come from CCMIntel's capital cost database (ISBL, OSBL, Other Project Costs and Working Capital, USD million, cost basis year shown).</li>
          <li>Total plant capital is scaled with the capacity-exponent rule: Total × (Target ÷ Base capacity)^n, default n = 0.6.</li>
          <li>The scaled total is split using the original case's component ratios. Optional escalation brings costs from the base year to a target year.</li>
          <li>Estimates are indicative (±30%) and intended for screening, not for investment decisions.</li>
        </ul>
      </Panel>
    </div>
  );
}

export default function Capex() {
  return <ProductGate module="capex" eyebrow="CAPEX Calculator" title="CAPEX Calculator">{(p) => <CapexView {...p} />}</ProductGate>;
}
